import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PracticalAssessment } from "../components/rm/PracticalAssessment";
import { api } from "../lib/api";
import { ArrowLeft, Clock, ClipboardCheck } from "lucide-react";

export default function RMTrainingModule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [error, setError] = useState(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setMod(null); setError(null); setReachedEnd(false);
    api.get(`/rm/training/modules/${id}`)
      .then((r) => setMod(r.data))
      .catch((e) => setError(e.response?.status === 423
        ? "This module is locked — pass the previous module's quiz first."
        : "Couldn't load this module."));
  }, [id]);

  useEffect(() => {
    if (!mod || !sentinelRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setReachedEnd(true); },
      { threshold: 0.1 });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [mod]);

  return (
    <div className="min-h-screen bg-[#F4F8F8]" data-testid="rm-training-module-page">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3">
          <Link to="/rm/training" className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-[#0A8B85]"
            data-testid="rm-module-back-btn">
            <ArrowLeft size={14} /> All modules
          </Link>
          {mod && (
            <p className="text-xs font-semibold text-slate-400">
              {mod.completed_count}/{mod.total_modules} completed
              {mod.days_remaining != null && <span className="ml-2 text-[#0A8B85]">· {mod.days_remaining} days left</span>}
            </p>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        {error ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="rm-module-locked-msg">
            <p className="text-sm font-semibold text-amber-800">{error}</p>
            <Link to="/rm/training" className="mt-3 inline-block rounded-lg bg-[#0A8B85] px-4 py-2 text-xs font-bold text-white">
              Back to training
            </Link>
          </div>
        ) : !mod ? (
          <p className="py-12 text-center text-slate-400">Loading…</p>
        ) : (
          <article className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#0A8B85]">Module {mod.id} of {mod.total_modules}</p>
            <h1 className="mt-1 text-2xl font-extrabold text-[#0A2540]" data-testid="rm-module-title">{mod.title}</h1>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400" data-testid="rm-module-reading-time">
              <Clock size={13} /> approximately {mod.minutes} minutes
            </p>

            <div className="prose-training mt-6 text-[15px] leading-7 text-slate-700"
              data-testid="rm-module-content"
              dangerouslySetInnerHTML={{ __html: mod.content }} />

            <div ref={sentinelRef} className="h-1" data-testid="rm-module-end-sentinel" />

            {mod.practical && <PracticalAssessment readonly={mod.readonly} />}

            {reachedEnd && !mod.practical && (
              <div className="mt-8 border-t border-slate-100 pt-6 text-center">
                {mod.status === "completed" ? (
                  <p className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-5 py-3 text-sm font-bold text-emerald-700" data-testid="rm-module-completed-note">
                    <ClipboardCheck size={17} /> Module completed — quiz score {mod.quiz_score}%
                  </p>
                ) : (
                  <>
                    <button
                      onClick={() => navigate(`/rm/training/module/${mod.id}/quiz`)}
                      className="inline-flex items-center gap-2 rounded-xl bg-[#0A8B85] px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#087a74]"
                      data-testid="rm-module-start-quiz-btn">
                      <ClipboardCheck size={17} /> Start Quiz
                    </button>
                    <p className="mt-2 text-[11px] text-slate-400">10 questions · pass mark 80% · pass to unlock the next module.</p>
                  </>
                )}
              </div>
            )}
          </article>
        )}
      </main>
      <style>{`
        .prose-training h3 { font-size: 1.05rem; font-weight: 800; color: #0A2540; margin: 1.6em 0 0.5em; }
        .prose-training h4 { font-size: 0.95rem; font-weight: 700; color: #0A8B85; margin: 1.2em 0 0.4em; }
        .prose-training em { color: #64748b; }
        .prose-training p { margin: 0.7em 0; }
        .prose-training ul, .prose-training ol { margin: 0.7em 0; padding-left: 1.4em; }
        .prose-training ul { list-style: disc; }
        .prose-training ol { list-style: decimal; }
        .prose-training li { margin: 0.35em 0; }
        .prose-training strong { color: #0A2540; }
      `}</style>
    </div>
  );
}
